import { useEffect } from 'react';

// Dedicated sessionStorage key — NOT the `Session` store (which `invalidate()`
// clears) — so the value survives logout's `session.invalidate()` and the
// full-document redirect to `/login`. Mirrors Ember's
// `session.set('lastVisitedRoute', window.location.pathname)`, written on every
// authenticated route change (`app` controller `onRouteDidChange`), and read by
// `handleInvalidation` to send the user back after re-login.
const LAST_VISITED_ROUTE_KEY = 'cio-last-visited-route';

/**
 * Record the current in-app route so an explicit logout can return the user to
 * it after re-login. The auth entry points are never recorded — otherwise a
 * fresh `/logout` page load would overwrite the real prior route.
 */
export function recordLastVisitedRoute(pathname: string): void {
  if (pathname.startsWith('/login') || pathname.startsWith('/logout')) {
    return;
  }
  try {
    window.sessionStorage.setItem(LAST_VISITED_ROUTE_KEY, pathname);
  } catch {
    // sessionStorage unavailable (private mode / SSR) — best-effort only.
  }
}

export function getLastVisitedRoute(): string | null {
  try {
    return window.sessionStorage.getItem(LAST_VISITED_ROUTE_KEY);
  } catch {
    return null;
  }
}

/**
 * Records the current authenticated route on every change so logout can send
 * the user back to it after re-login (Ember's `lastVisitedRoute` →
 * `/login?origin=…`). Mount once in the authenticated layout.
 */
export function useRecordLastVisitedRoute(pathname: string): void {
  useEffect(() => {
    recordLastVisitedRoute(pathname);
  }, [pathname]);
}
